import cryptoJs from 'crypto-js';
import {
    ChunkedFileReader,
    CHUNKED_FILE_READER_EVENTS
} from './ChunkedFileReader';

const base64urlHash = (hash) => {
    return hash.replace(/[+/=]/g, (m0) => {
        switch (m0) {
            case '+':
                return '-';
            case '/':
                return '_';
            case '=':
                return '';
            default:
                throw new Error(`unexpected base 64 char ${m0}`);
        }
    });
};

export function calculateFileHash(file) {
    if (!file || file.size <= 0) {
        // eslint-disable-next-line i18next/no-literal-string
        throw new Error('Cannot process empty file');
    }

    let SHA256 = cryptoJs.algo.SHA256.create();

    // Detect if browser is Safari to adjust chunk size
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // Use smaller chunks for Safari to improve performance
    const maxChunkSize = isSafari ? 1024 * 256 : 2048 * 1024; // 256KB for Safari, 2MB for others

    // Allow concurrent processing for non-Safari browsers
    const concurrentReads = isSafari ? 1 : 3;

    return new Promise((resolve) => {
        const reader = new ChunkedFileReader({
            maxChunkSize: maxChunkSize,
            concurrentReads: concurrentReads
        });

        reader.subscribe(CHUNKED_FILE_READER_EVENTS.PROGRESS, function (evt) {
            console.log(
                `Progress ${evt.done} / ${evt.nchunks} chunks (${(
                    evt.done_ratio * 100
                ).toFixed(2)}%)`
            );
        });

        // Use a more efficient approach for updating the hash
        const pendingChunks = new Map();
        let nextSequenceToProcess = 1;

        reader.subscribe(CHUNKED_FILE_READER_EVENTS.CHUNK, function (e) {
            // Store chunks in order to process them sequentially
            pendingChunks.set(e.seq, e.chunk);

            // Process chunks in sequence order
            processNextChunks();
        });

        // Define processChunk function outside of the loop context
        const processChunk = () => {
            if (!pendingChunks.has(nextSequenceToProcess)) return;

            const data = pendingChunks.get(nextSequenceToProcess);
            const wordBuffer = cryptoJs.lib.WordArray.create(data);
            SHA256.update(wordBuffer);

            // Remove processed chunk from memory
            pendingChunks.delete(nextSequenceToProcess);
            nextSequenceToProcess++;

            // Process next chunk with a small delay
            if (pendingChunks.has(nextSequenceToProcess)) {
                setTimeout(processChunk, 5);
            }
        };

        function processNextChunks() {
            // For Safari, process chunks with a small delay to prevent UI freezing
            if (isSafari && pendingChunks.has(nextSequenceToProcess)) {
                processChunk();
            } else {
                // For other browsers, process all available chunks in sequence
                while (pendingChunks.has(nextSequenceToProcess)) {
                    const data = pendingChunks.get(nextSequenceToProcess);
                    const wordBuffer = cryptoJs.lib.WordArray.create(data);
                    SHA256.update(wordBuffer);

                    // Remove processed chunk from memory
                    pendingChunks.delete(nextSequenceToProcess);
                    nextSequenceToProcess++;
                }
            }
        }

        reader.subscribe(CHUNKED_FILE_READER_EVENTS.END, function () {
            // For Safari, we need to check if all chunks have been processed
            if (isSafari && pendingChunks.size > 0) {
                // Set up a check that runs every 50ms to see if all chunks are processed
                const checkAllProcessed = () => {
                    if (pendingChunks.size === 0) {
                        // All chunks processed, finalize hash
                        const encrypted = SHA256.finalize();
                        const hash = base64urlHash(
                            cryptoJs.enc.Base64.stringify(encrypted)
                        );
                        console.log(
                            `Hash of file ${file.name}, ${file.type} (${file.size}B) is ${hash}`
                        );
                        resolve(hash);
                    } else {
                        // Still waiting for chunks to process
                        setTimeout(checkAllProcessed, 50);
                    }
                };

                setTimeout(checkAllProcessed, 50);
            } else {
                // For other browsers, ensure all chunks are processed immediately
                processNextChunks();

                const encrypted = SHA256.finalize();
                const hash = base64urlHash(
                    cryptoJs.enc.Base64.stringify(encrypted)
                );
                console.log(
                    `Hash of file ${file.name}, ${file.type} (${file.size}B) is ${hash}`
                );
                resolve(hash);
            }
        });

        reader.readChunks(file);
    });
}
